import { useEffect, useState } from "react";
import {
    Box,
    Heading,
    FormControl,
    FormLabel,
    FormHelperText,
    FormErrorMessage,
    Input,
    Switch,
    Button,
    Flex,
    SimpleGrid,
    Alert,
    AlertIcon,
    AlertDescription,
} from "@chakra-ui/react";

const SettingsCard = ({ settings, onSave }) => {
    const [formData, setFormData] = useState(settings);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setFormData(settings);
    }, [settings]);

    if (!formData) return null;

    const objectType = formData.priceListObjectType || "";
    const objectCode = formData.priceListObjectCode || "";
    const isCodeMissing = !objectCode.trim();
    const isDirty =
        formData.shouldGeneratePriceList !== settings.shouldGeneratePriceList ||
        objectType !== (settings.priceListObjectType || "") ||
        objectCode !== (settings.priceListObjectCode || "");

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave({
                shouldGeneratePriceList: formData.shouldGeneratePriceList,
                priceListObjectType: objectType.trim(),
                priceListObjectCode: objectCode.trim(),
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" boxShadow="card" p={5} mx={4} mb={6}>
            <Heading size="sm" color="brand.700" mb={4}>Postavke</Heading>

            {!(settings.priceListObjectType || "").trim() && (
                <Alert status="warning" borderRadius="lg" mb={4}>
                    <AlertIcon />
                    <AlertDescription fontSize="sm">
                        Vrsta objekta nije postavljena. Vrsta i oznaka objekta ulaze u propisani naziv datoteke koji se
                        generira u trenutku uploada i kasnije se ne mijenja - ispunite ih prije prvog uploada.
                    </AlertDescription>
                </Alert>
            )}

            <FormControl display="flex" alignItems="center" mb={4}>
                <Switch
                    id="shouldGeneratePriceList"
                    colorScheme="teal"
                    mr={3}
                    isChecked={!!formData.shouldGeneratePriceList}
                    onChange={(e) => setFormData({ ...formData, shouldGeneratePriceList: e.target.checked })}
                />
                <Box>
                    <FormLabel htmlFor="shouldGeneratePriceList" mb={0}>Automatski objavi cjenik svaki dan</FormLabel>
                    <FormHelperText mt={0}>
                        Ako za današnji dan nitko nije učitao cjenik, zadnji cjenik se kopira kao današnji.
                    </FormHelperText>
                </Box>
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                <FormControl>
                    <FormLabel>Vrsta objekta</FormLabel>
                    <Input
                        placeholder="npr. RESTORAN, CAFFE BAR, SALON"
                        maxLength={50}
                        value={objectType}
                        onChange={(e) => setFormData({ ...formData, priceListObjectType: e.target.value })}
                    />
                    <FormHelperText>Koristi se u nazivu datoteke (max 50 znakova).</FormHelperText>
                </FormControl>

                <FormControl isRequired isInvalid={isCodeMissing}>
                    <FormLabel>Oznaka objekta</FormLabel>
                    <Input
                        placeholder="P-01"
                        maxLength={20}
                        value={objectCode}
                        onChange={(e) => setFormData({ ...formData, priceListObjectCode: e.target.value })}
                    />
                    {isCodeMissing ? (
                        <FormErrorMessage>Oznaka objekta je obavezna.</FormErrorMessage>
                    ) : (
                        <FormHelperText>Interna oznaka objekta (max 20 znakova).</FormHelperText>
                    )}
                </FormControl>
            </SimpleGrid>

            <Flex justify="flex-end">
                <Button
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: "brand.700" }}
                    onClick={handleSave}
                    isLoading={isSaving}
                    loadingText="Spremanje..."
                    isDisabled={isSaving || isCodeMissing || !isDirty}
                >
                    Spremi postavke
                </Button>
            </Flex>
        </Box>
    );
};

export default SettingsCard;
